import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Plus, Search } from "lucide-react";
import { toast } from "react-hot-toast";
import { useLanguage } from "../../contexts/LanguageContext";
import { widgetConfigApi } from "../../services/widgetConfigApi";
import { apiErrorMessage } from "../../utils/apiErrorMessage";
import PageContextCard from "./PageContextCard";

const PAGE_SIZE = 50;

// Os campos que um cartão edita. O `id` é do servidor, e a ordem também: a
// plataforma mantém-na (RPAA-247), e mudar de lugar não é uma edição.
const editableFields = (ctx) => ({
  name: ctx.name ?? null,
  urlPatterns: ctx.urlPatterns || [],
  quickReplies: ctx.quickReplies || [],
});

// As linhas vazias saem ANTES de gravar, como o `stripBlanks` do Save da página
// faz para as replies por omissão. Sem isto, uma reply em branco chegava ao
// servidor tal e qual, e o servidor aceita "" (é uma string), portanto o widget
// desenhava um chip sem texto. Trim no save e nunca a cada tecla: aparar um
// input controlado enquanto se escreve come os espaços entre as palavras.
const withoutBlanks = (fields) => {
  const keep = (arr) =>
    (arr || [])
      .map((s) => (typeof s === "string" ? s.trim() : s))
      .filter((s) => typeof s === "string" && s);
  return { ...fields, urlPatterns: keep(fields.urlPatterns), quickReplies: keep(fields.quickReplies) };
};

const sameFields = (a, b) =>
  JSON.stringify(editableFields(a)) === JSON.stringify(editableFields(b));

const withoutDraft = (drafts, id) => {
  const next = { ...drafts };
  delete next[id];
  return next;
};

/**
 * Substitui o editor que carregava a lista inteira no formulário da página
 * (RPAA-247).
 *
 * Duas diferenças que mudam o modelo mental de quem usa isto, e ambas são
 * deliberadas:
 *
 * 1. A lista vem do SERVIDOR, paginada e pesquisada lá. Com ~8.300 contextos, o
 *    editor antigo desenhava 8.300 cabeçalhos e voltava a desenhá-los a cada
 *    tecla escrita em qualquer campo da secção Copy.
 *
 * 2. Cada cartão GUARDA-SE SOZINHO, contra a sua própria rota. O Save da página
 *    deixa de transportar os contextos — e portanto deixa de os poder perder.
 *    O custo: há dois sítios para guardar nesta página, e quem editar um
 *    contexto e carregar no Save lá em baixo fica a achar que guardou. É por
 *    isso que o cartão por gravar mostra o estado em vez de o esconder.
 */
const PageContextsBrowser = ({ chatId, onCountChange, onUnsavedChange }) => {
  const { translate } = useLanguage();
  // A página em ecrã guarda o offset e a pesquisa COM QUE FOI PEDIDA, e não os
  // que o estado tem agora. Entre mudar de página (ou limpar a pesquisa) e a
  // resposta chegar, os dois divergem, e o índice com que o save compara a posição
  // devolvida tem de descrever a lista que o operador está a ver.
  const [listing, setListing] = useState({ items: [], total: 0, offset: 0, query: "" });
  const { items, total } = listing;
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  // O cartão acabado de criar, até ele aparecer no ecrã. Abrir o cartão e ir
  // para a última página não chegava: medido no browser com 120 contextos, o
  // editor novo ficava 200px abaixo do fundo da janela e sem foco, portanto o
  // operador carregava em Adicionar e continuava sem ver nada.
  const [revealId, setRevealId] = useState(null);
  const listRef = useRef(null);
  const [error, setError] = useState(null);
  // Só a resposta do pedido mais recente escreve. O debounce reduz os pedidos
  // mas não os ordena, e o load também é chamado à mão depois de cada escrita:
  // uma pesquisa lenta por "tv" que chegue depois da lista vazia que a seguiu
  // punha no ecrã os resultados de "tv" e o total deles, com a caixa vazia.
  const latestRequest = useRef(0);
  // A página e a pesquisa que o ecrã quer AGORA, lidas pelo `load` no momento
  // do pedido e não fechadas no render em que ele foi criado. O save de um
  // cartão e o apagar chamam o `load` depois de um pedido de escrita,
  // e o `load` que têm é o do render em que o clique aconteceu. Se entretanto a
  // pesquisa mudou, esse load antigo saía DEPOIS do da pesquisa nova, ganhava a
  // guarda acima por ser o mais recente, e punha no ecrã os resultados da
  // pesquisa anterior com a caixa a dizer outra coisa.
  const wanted = useRef({ page: 0, query: "" });
  wanted.current = { page, query };
  // A lista em ecrã AGORA, pela mesma razão: o save lê-a depois de
  // um pedido de escrita, com os handlers do render em que o clique aconteceu.
  const shownListing = useRef(listing);
  shownListing.current = listing;
  // Há um pedido em voo, e é o mais recente. Só o mais recente o desliga, pela
  // mesma razão por que só ele escreve a lista.
  const [loading, setLoading] = useState(false);
  // Os contextos do chat inteiro, com ou sem pesquisa. Lido de cada resposta
  // sem pesquisa e acertado pelas criações e remoções feitas aqui entretanto. É
  // o que o PageContextsIO mostra, e é o que diz em que página cai um contexto
  // acabado de criar.
  const [chatTotal, setChatTotal] = useState(null);
  // As edições por gravar, por id de contexto: só os campos editáveis, e só dos
  // cartões que diferem do que está gravado. Vivem aqui e não em cada cartão
  // para sobreviverem ao cartão sair do ecrã (outra página, uma pesquisa, uma
  // reordenação que o leva para outra página) e a qualquer recarregamento da
  // lista. Um rascunho sujo nunca é reposto pelo servidor: só sai ao gravar, ao
  // descartar, ou ao apagar o contexto.
  const [drafts, setDrafts] = useState({});
  const unsavedCount = Object.keys(drafts).length;

  // A página usa isto para avisar antes de trocar de chat ou fechar o
  // separador, e para bloquear o import, que substitui os contextos todos.
  useEffect(() => {
    onUnsavedChange(unsavedCount > 0);
  }, [unsavedCount, onUnsavedChange]);
  useEffect(() => () => onUnsavedChange(false), [onUnsavedChange]);

  useEffect(() => {
    if (chatTotal !== null) onCountChange(chatTotal);
  }, [chatTotal, onCountChange]);

  // O debounce é sobre a PESQUISA e não sobre o pedido: sem ele, escrever
  // "televisores" dispara onze pedidos e a resposta que chega por último não é
  // necessariamente a do que está escrito.
  //
  // Sai logo quando a caixa já coincide com a pesquisa aplicada. É o caso do
  // `add`, que limpa as duas de uma vez e escolhe a página: sem esta saída, o
  // timer voltava 300 ms depois à página 0 e tirava o contexto novo do ecrã.
  useEffect(() => {
    if (search === query) return;
    const t = setTimeout(() => {
      setQuery(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [search, query]);

  const load = useCallback(async () => {
    const { page, query } = wanted.current;
    const request = ++latestRequest.current;
    setLoading(true);
    try {
      const data = await widgetConfigApi.listPageContexts(chatId, {
        offset: page * PAGE_SIZE,
        limit: PAGE_SIZE,
        q: query || undefined,
      });
      if (request !== latestRequest.current) return;
      // Só sem pesquisa: com ela, o `total` é o dos resultados.
      if (!query) setChatTotal(data.total);
      // A lista encolheu para trás da página pedida: apagar o único cartão da
      // última página, ou outra sessão a apagar entretanto. Sem isto o `page`
      // ficava onde estava, o pedido saía com um offset depois do fim, e o ecrã
      // ficava vazio com o paginador a ler "3 / 2", como se tivesse apagado
      // tudo. Não escreve a lista vazia: muda a página, e o efeito pede a
      // última que existe.
      const lastPage = Math.max(0, Math.ceil(data.total / PAGE_SIZE) - 1);
      // O `loading` fica ligado: o pedido da página certa sai já a seguir.
      if (page > lastPage) {
        setPage(lastPage);
        return;
      }
      setListing({ items: data.items, total: data.total, offset: page * PAGE_SIZE, query });
      setError(null);
      setLoading(false);
    } catch (e) {
      if (request !== latestRequest.current) return;
      setError(e?.message || "failed");
      setLoading(false);
    }
  }, [chatId]);

  // A página e a pesquisa entram aqui e não no `load`, que as lê do `wanted`.
  useEffect(() => {
    load();
  }, [load, page, query]);

  // Uma escrita recusada diz porquê. O servidor escreve o motivo por extenso (o
  // tecto de contextos, o de padrões, o de tokens por padrão), e só quem está a
  // editar o pode corrigir.
  const reportFailure = (err, fallback) => {
    console.error("Page context request failed", err);
    toast.error(apiErrorMessage(err, fallback));
  };

  const add = async () => {
    let created;
    try {
      created = await widgetConfigApi.createPageContext(chatId, {
        urlPatterns: [],
        quickReplies: [],
      });
    } catch (err) {
      reportFailure(err, translate("pageContextAddFailed") || "Could not add the context");
      return;
    }
    // O servidor cria na precedência mais baixa, ou seja no fim da lista
    // completa. A pesquisa sai, porque um contexto vazio não casa com nenhuma, e
    // a vista vai para a última página, onde ele está, com o cartão aberto. Ficar
    // onde se estava deixava o operador a olhar para uma lista que não mudou, e
    // cada novo clique em Adicionar gravava mais uma linha vazia.
    const newTotal = (chatTotal ?? 0) + 1;
    const lastPage = Math.floor((newTotal - 1) / PAGE_SIZE);
    setChatTotal(newTotal);
    setOpenId(created.id);
    setRevealId(created.id);
    setSearch("");
    setQuery("");
    // Recarrega em vez de inserir à mão: a posição é atribuída pelo servidor, e
    // adivinhá-la aqui era a forma de a lista discordar do que está gravado.
    // Mudar a página ou a pesquisa já recarrega pelo efeito; só quando nenhuma
    // das duas muda é que é preciso pedir.
    if (lastPage === page && !query) await load();
    else setPage(lastPage);
  };

  // Confirmado antes, com o mesmo window.confirm do StoreLocationsManagement: o
  // DELETE é imediato, o Descartar da página já não lhe toca, e o X fica
  // encostado ao cabeçalho que abre o cartão.
  const remove = async (id, label) => {
    if (
      !window.confirm(
        translate("pageContextDeleteConfirm", { name: label }) ||
          `Are you sure you want to delete the context "${label}"? Its patterns and quick replies are deleted immediately. This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      await widgetConfigApi.deletePageContext(chatId, id);
    } catch (err) {
      reportFailure(err, translate("pageContextDeleteFailed") || "Could not delete the context");
      return;
    }
    // Com pesquisa, o load a seguir não actualiza a contagem do chat.
    setChatTotal((count) => (count === null ? null : count - 1));
    setDrafts((prev) => withoutDraft(prev, id));
    if (openId === id) setOpenId(null);
    await load();
  };

  const changeDraft = (saved, next) =>
    setDrafts((prev) =>
      sameFields(next, saved)
        ? withoutDraft(prev, saved.id)
        : { ...prev, [saved.id]: editableFields(next) }
    );

  const discardDraft = (id) => setDrafts((prev) => withoutDraft(prev, id));

  // `sent` é o rascunho tal como estava quando o save partiu, e `persisted` é o
  // que o servidor recebeu, já sem as linhas vazias. São dois de propósito: a
  // lista mostra o gravado, mas a pergunta "foi escrito alguma coisa enquanto o
  // pedido estava em voo" compara-se contra o rascunho enviado. Com um só valor,
  // um rascunho com uma linha vazia nunca era igual ao gravado e o cartão ficava
  // para sempre por gravar.
  // O servidor pode ter reordenado outros contextos além do gravado, sobretudo na
  // primeira escrita num chat importado antes do RPAA-247. Sem isto a lista
  // mudava por baixo do operador sem explicação. Um backend que não mande o número
  // não mostra nada.
  const announceOthersReordered = (reordered) => {
    if (!Number.isInteger(reordered) || reordered <= 0) return;
    toast(
      translate("pageContextsOthersReordered", { count: reordered }) ||
        `Other contexts changed order so that none sits below one that covers it: ${reordered}.`
    );
  };

  const markSaved = async (id, sent, persisted, position, reordered) => {
    // Onde o contexto está na lista em ecrã quando a resposta chega, e não no
    // render do clique: dois saves seguidos davam ao segundo o aviso de uma
    // mudança que foi o primeiro a fazer. Só na lista completa: com pesquisa, o
    // índice em ecrã não é o da lista.
    const now = shownListing.current;
    const index = now.items.findIndex((c) => c.id === id);
    const was = now.query || index < 0 ? null : now.offset + index;
    // Aplicado à mão antes de recarregar, para o cartão não mostrar a versão
    // antiga entre o rascunho sair e a resposta chegar.
    setListing((prev) => ({
      ...prev,
      items: prev.items.map((c) => (c.id === id ? { ...c, ...persisted } : c)),
    }));
    // Só se nada foi escrito enquanto o pedido estava em voo: aí o rascunho já
    // não é o que se gravou, e continua por gravar.
    setDrafts((prev) =>
      prev[id] && JSON.stringify(prev[id]) === JSON.stringify(sent)
        ? withoutDraft(prev, id)
        : prev
    );
    announceOthersReordered(reordered);
    // Padrões novos podem mudar o contexto de lugar: mais estreitos do que os de
    // um contexto acima, o servidor sobe-o para cima dele; mais largos do que os
    // de um abaixo, desce-o (RPAA-247). A vista segue-o, e diz porquê. Um backend
    // anterior a isto não devolve a posição, e aí não há nada a seguir: os dois
    // repos não saem sempre no mesmo deploy.
    if (was !== null && Number.isInteger(position) && position !== was) {
      toast(
        position < was
          ? translate("pageContextMovedUpOnSave") ||
              "The context moved up the list: the narrower context sits above the broader one."
          : translate("pageContextMovedDownOnSave") ||
              "The context moved down the list: the narrower context sits above the broader one."
      );
      setRevealId(id);
      const landedPage = Math.floor(position / PAGE_SIZE);
      // A página que o ecrã quer agora: o operador pode ter mudado de página
      // enquanto o PATCH estava em voo.
      if (landedPage !== wanted.current.page) {
        setPage(landedPage);
        return;
      }
    }
    await load();
  };

  // Espera que o cartão novo esteja na lista em ecrã, que só acontece depois do
  // load da última página. Faz scroll até ele e põe o foco no nome, que é o
  // primeiro campo que o operador vai preencher.
  useEffect(() => {
    if (!revealId || !items.some((c) => c.id === revealId)) return;
    const node = listRef.current?.querySelector(`[data-context-id="${revealId}"]`);
    if (!node) return;
    node.scrollIntoView({ block: "center" });
    node.querySelector("input")?.focus({ preventScroll: true });
    setRevealId(null);
  }, [revealId, items]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // A lista em ecrã já não é a que o operador pediu: a pesquisa está à espera
  // do debounce, ou o pedido da página ou da pesquisa ainda não voltou. Sem isto
  // o ecrã mostrava a resposta anterior como se fosse a da caixa, incluindo um
  // "0 contextos" de uma pesquisa que já não é a escrita. As recargas depois de
  // uma escrita não contam: pedem a mesma lista que está em ecrã, e escurecê-la
  // a cada save era um piscar sem informação nenhuma.
  //
  // O `loading` sozinho não chega. Quando o debounce dispara, o render em que
  // `query` já é o da caixa ainda tem o `loading` a false (quem o liga é o
  // efeito, no render seguinte), e esse render mostrava "0 contextos" da
  // pesquisa anterior. Sem erro, uma lista que não é a pedida está sempre à
  // espera de um pedido; com erro, não está à espera de nada.
  const showingWanted = listing.query === query && listing.offset === page * PAGE_SIZE;
  const listPending = search !== query || (!showingWanted && (loading || !error));
  // O contador passa a "A pesquisar..." só quando é a PESQUISA que muda. Mudar
  // de página numa pesquisa não muda o total, e o número pode ficar.
  const searchPending = listPending && search !== listing.query;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: "var(--color-text-muted)" }}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={
              translate("pageContextsSearch") || "Search by name or pattern"
            }
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border"
            style={{ borderColor: "var(--color-border)" }}
          />
        </div>
        {/* Só com pesquisa: sem ela, a contagem do chat já está no
            PageContextsIO por cima, e dois números iguais seguidos leem-se
            como duas coisas diferentes. Depois de um erro não há número: o
            que estava era o de outro pedido. A região viva existe sempre,
            porque um leitor de ecrã só anuncia mudanças dentro de uma região
            que já lá estava. */}
        <span role="status" aria-live="polite">
          {searchPending ? (
            search && (
              <span
                className="flex items-center gap-1.5 text-xs whitespace-nowrap"
                style={{ color: "var(--color-text-muted)" }}
              >
                <Loader2 size={12} className="animate-spin" />
                {translate("searching") || "Searching..."}
              </span>
            )
          ) : (
            listing.query &&
            !error && (
              <span className="text-xs whitespace-nowrap" style={{ color: "var(--color-text-muted)" }}>
                {total} {translate("pageContextsCount") || "contexts"}
              </span>
            )
          )}
        </span>
      </div>

      {unsavedCount > 0 && (
        <p className="text-xs" style={{ color: "var(--color-warning)" }}>
          {translate("pageContextsUnsavedCount", { count: unsavedCount }) ||
            `${unsavedCount} contexts with unsaved edits`}
        </p>
      )}

      {error && (
        <p className="text-xs" style={{ color: "var(--color-error)" }}>
          {translate("pageContextsLoadError") || "Could not load contexts."}
        </p>
      )}

      {/* O primeiro pedido, antes de haver lista nenhuma: sem isto a secção
          ficava vazia, sem contagem nem cartões, até ele voltar. Não lê o
          `loading`, que no primeiro render ainda está a false. */}
      {chatTotal === null && !error && (
        <p
          className="flex items-center gap-1.5 text-xs py-2"
          style={{ color: "var(--color-text-muted)" }}
        >
          <Loader2 size={12} className="animate-spin" />
          {translate("loading") || "Loading..."}
        </p>
      )}

      {/* Só depois de uma resposta que disse mesmo zero para a pesquisa da
          caixa. */}
      {!listPending && !error && listing.query && total === 0 && (
        <p className="text-sm py-2" style={{ color: "var(--color-text-muted)" }}>
          {translate("pageContextsNoMatch") || "No contexts match this search."}
        </p>
      )}

      {/* A lista anterior fica, esbatida, até a nova chegar: esvaziá-la a cada
          tecla fazia a página saltar e lia-se como "não há resultados". Depois
          de um erro continua esbatida, porque não é a lista que a caixa pede. */}
      <div
        className={`space-y-2 transition-opacity ${listPending || !showingWanted ? "opacity-50" : ""}`}
        aria-busy={listPending}
        ref={listRef}
      >
        {items.map((ctx, idx) => {
          const draft = drafts[ctx.id];
          const shownName =
            (draft ? draft.name : ctx.name) ||
            `${translate("pageContext") || "Context"} ${listing.offset + idx + 1}`;
          return (
            <div key={ctx.id} data-context-id={ctx.id}>
            <SelfSavingCard
              chatId={chatId}
              saved={ctx}
              draft={draft}
              onDraftChange={(next) => changeDraft(ctx, next)}
              onDiscard={() => discardDraft(ctx.id)}
              onSaved={(sent, persisted, position, reordered) =>
                markSaved(ctx.id, sent, persisted, position, reordered)
              }
              index={listing.offset + idx}
              isOpen={openId === ctx.id}
              onToggle={() => setOpenId(openId === ctx.id ? null : ctx.id)}
              onRemove={() => remove(ctx.id, shownName)}
            />
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1 rounded disabled:opacity-40"
          >
            {translate("previous") || "Previous"}
          </button>
          <span style={{ color: "var(--color-text-muted)" }}>
            {page + 1} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 rounded disabled:opacity-40"
          >
            {translate("next") || "Next"}
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={add}
        className="flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg transition-colors hover:bg-gray-100"
        style={{ color: "var(--color-mint)" }}
      >
        <Plus size={14} />
        {translate("addPageContext") || "Add context"}
      </button>
    </div>
  );
};

/**
 * Um cartão com botão de gravar e de descartar o seu rascunho.
 *
 * O rascunho não é deste componente, é do PageContextsBrowser (ver `drafts`):
 * aqui só se mostra e se grava. Sem rascunho, cada tecla era um PATCH. E os
 * botões só aparecem quando há algo por gravar: um botão sempre visível e quase
 * sempre inútil ensina a ignorá-lo, que é o contrário do que aqui é preciso.
 */
const SelfSavingCard = ({ chatId, saved, draft, onDraftChange, onDiscard, onSaved, ...cardProps }) => {
  const { translate } = useLanguage();
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const sent = draft;
    const fields = withoutBlanks(draft);
    setSaving(true);
    try {
      const { position, reordered } = await widgetConfigApi.updatePageContext(chatId, saved.id, fields);
      await onSaved(sent, fields, position, reordered);
    } catch (err) {
      // O rascunho fica como está: a edição continua no ecrã para ser corrigida.
      console.error("Page context save failed", err);
      toast.error(
        apiErrorMessage(err, translate("pageContextSaveFailed") || "Could not save the context")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageContextCard
        {...cardProps}
        context={draft ? { ...saved, ...draft } : saved}
        onChange={onDraftChange}
      />
      {draft && (
        <div className="flex justify-end gap-2 mt-3 mb-1">
          <button
            type="button"
            onClick={onDiscard}
            disabled={saving}
            className="text-xs font-medium px-3 py-1 rounded-lg disabled:opacity-50"
            style={{ color: "var(--color-text-secondary)" }}
          >
            {translate("discard") || "Discard"}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="text-xs font-medium px-3 py-1 rounded-lg disabled:opacity-50"
            style={{ backgroundColor: "var(--color-mint)", color: "white" }}
          >
            {saving
              ? translate("saving") || "Saving…"
              : translate("savePageContext") || "Save this context"}
          </button>
        </div>
      )}
    </div>
  );
};

export default PageContextsBrowser;
